/**
 * Judge Rubrics Tab
 *
 * Settings tab for managing LLM Judge evaluation rubrics.
 * Allows users to create, edit, and delete rubrics.
 */
import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiAlertTriangle,
  FiTarget, FiCheckCircle, FiXCircle
} from 'react-icons/fi';
import {
  getJudgeRubrics,
  createJudgeRubric,
  updateJudgeRubric,
  deleteJudgeRubric,
  getCredentials,
  getAvailableModels
} from '../../../services/aiOperationsApi';
import RubricEditorModal, { ExistingRubric, RubricCredential, AvailableModel as RubricAvailableModel } from '../../ai-operations/components/evaluations/RubricEditorModal';

interface RubricCriterion {
  name: string;
  weight?: number;
  description?: string;
}

interface Rubric {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  judge_provider?: string;
  judge_model_id?: string;
  aggregation_method?: string;
  criteria?: RubricCriterion[];
}

interface Credential {
  id: string;
  is_enabled: boolean;
  provider?: string;
}

interface AvailableModel {
  id: string;
  name?: string;
  provider?: string;
}

/**
 * Provider display labels
 */
const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  bedrock: 'Bedrock'
};

const JudgeRubricsTab: React.FC = () => {
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRubric, setEditingRubric] = useState<Rubric | null>(null);
  const [saving, setSaving] = useState(false);

  // Load rubrics and credentials on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load credentials and available models (always available)
      const [credentialsRes, modelsRes] = await Promise.all([
        getCredentials(),
        getAvailableModels()
      ]);
      setCredentials((credentialsRes || []).filter((c: Credential) => c.is_enabled));
      setAvailableModels(modelsRes?.models || modelsRes || []);

      // Load rubrics - may fail if endpoint doesn't exist in this environment
      try {
        const rubricsRes = await getJudgeRubrics({ limit: 100 });
        const rubricsList = rubricsRes?.items || rubricsRes?.data || rubricsRes || [];
        setRubrics(rubricsList);
      } catch (rubricErr: unknown) {
        setRubrics([]);
        // Show a user-friendly message instead of error
        setError('Judge Rubrics feature is not available in this environment. Please ensure the backend is updated.');
      }
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadRubrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getJudgeRubrics({ limit: 100 });
      // Handle both {items: [...]} and direct array response
      const rubricsList = response?.items || response?.data || response || [];
      setRubrics(rubricsList);
    } catch (err: unknown) {
      console.error('Error loading rubrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load rubrics');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRubric(null);
    setShowModal(true);
  };

  const handleEdit = (rubric: Rubric) => {
    setEditingRubric(rubric);
    setShowModal(true);
  };

  const handleDelete = async (rubric: Rubric) => {
    if (!globalThis.confirm(`Are you sure you want to delete "${rubric.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteJudgeRubric(rubric.id);
      await loadRubrics();
    } catch (err: unknown) {
      console.error('Error deleting rubric:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete rubric');
    }
  };

  const handleSave = async (formData: object) => {
    setSaving(true);
    try {
      if (editingRubric) {
        // Update existing rubric
        await updateJudgeRubric(editingRubric.id, formData as Record<string, unknown>);
      } else {
        // Create new rubric
        await createJudgeRubric(formData as unknown as import('../../../services/aiOperationsApi').CreateJudgeRubricData);
      }
      setShowModal(false);
      setEditingRubric(null);
      await loadRubrics();
    } catch (err) {
      console.error('Error saving rubric:', err);
      throw err; // Re-throw so modal can display error
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRubric(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Judge Rubrics
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Define evaluation criteria for LLM Judge scoring method
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadRubrics}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <FiRefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Create Rubric
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <FiXCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {rubrics.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
          <FiTarget className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Judge Rubrics
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
            Judge Rubrics define how AI evaluates responses when using the LLM Judge scoring method in evaluations.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Create Your First Rubric
          </button>
        </div>
      )}

      {/* Rubrics Grid */}
      {rubrics.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {rubrics.map((rubric) => (
            <div
              key={rubric.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                    <FiTarget className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {rubric.name}
                    </h3>
                    {rubric.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                        <FiCheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <FiXCircle className="w-3 h-3" />
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(rubric)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(rubric)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {rubric.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {rubric.description}
                </p>
              )}

              {/* Metadata */}
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                  {rubric.criteria?.length || 0} criteria
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                  {(rubric.judge_provider && PROVIDER_LABELS[rubric.judge_provider]) || rubric.judge_provider || 'OpenAI'} / {rubric.judge_model_id || 'gpt-4o-mini'}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                  {rubric.aggregation_method || 'weighted_average'}
                </span>
              </div>

              {/* Criteria Preview */}
              {(rubric.criteria?.length ?? 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Criteria:</p>
                  <div className="flex flex-wrap gap-1">
                    {rubric.criteria!.slice(0, 4).map((criterion) => (
                      <span
                        key={criterion.name}
                        className="px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                      >
                        {criterion.name}
                      </span>
                    ))}
                    {rubric.criteria!.length > 4 && (
                      <span className="px-2 py-0.5 text-xs text-gray-500">
                        +{rubric.criteria!.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      <RubricEditorModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        rubric={editingRubric as ExistingRubric | null}
        loading={saving}
        credentials={credentials as RubricCredential[]}
        availableModels={availableModels as RubricAvailableModel[]}
      />
    </div>
  );
};

export default JudgeRubricsTab;
